/**
 * Terms acceptance gate — shown after admin approval, before dashboard access.
 */

import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { authApi } from '../services/api'

export const AcceptTerms = () => {
  const { user, refetch } = useAuth()
  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const termsVersion = user?.current_terms_version ?? ''

  const handleAccept = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await authApi.acceptTerms(termsVersion)
      await refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept terms')
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-slate-50 via-emerald-50 to-blue-50 -m-6 p-6">
      <div className="max-w-3xl mx-auto py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600">Please review and accept our terms before continuing.</p>
        </div>

        {/* Scrollable terms content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-gray-400 mb-6">Version: {termsVersion}</p>

          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">What This Service Does</h2>
            <p className="text-gray-600 leading-relaxed">
              This is an AI-assisted verification tool that helps you check whether source materials
              support specific claims. You provide the claims and sources; we analyse whether the
              sources contain evidence supporting or contradicting those claims.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              This tool does not determine objective truth. It analyses source-claim alignment based
              on the materials you provide.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">AI Involvement</h2>
            <p className="text-gray-600 leading-relaxed">
              This service uses artificial intelligence to analyse claims against source materials.
              AI systems can make mistakes — they may misread sources, miss context, or produce
              inaccurate results. You should:
            </p>
            <ul className="list-disc pl-6 text-gray-600 leading-relaxed mt-2">
              <li>Review AI assessments before relying on them</li>
              <li>Use the human validation feature to confirm or correct results</li>
              <li>Independently verify important claims</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Your Responsibilities</h2>
            <ul className="list-disc pl-6 text-gray-600 leading-relaxed">
              <li>You are responsible for the content you submit (claims, sources, documents)</li>
              <li>You must have the right to submit any content you provide</li>
              <li>You are responsible for how you use and share the results</li>
              <li>API and MCP access should respect reasonable rate limits</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Accuracy and Limitations</h2>
            <p className="text-gray-600 leading-relaxed">
              We provide this service in good faith but cannot guarantee accuracy. The service may
              be unavailable at times and results may contain errors. We recommend human review for
              any high-stakes decisions.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              We are not liable for decisions you make based on the service&apos;s output, except
              where liability cannot be excluded by law (such as for fraud or negligence causing
              personal injury).
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Your Data</h2>
            <p className="text-gray-600 leading-relaxed">
              To verify claims, we send the content you submit to third-party AI providers for
              analysis. We also log AI interactions to monitor quality and debug issues. We store
              verification results to provide the service and allow you to access your history.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              We do not use your content to train AI models and we do not sell your data. You can
              request deletion of your data by contacting us.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update these terms from time to time. Continued use of the service after
              changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These terms are governed by the laws of England and Wales. Any disputes will be
              subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>
        </div>

        {/* Acceptance checkbox and button */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-gray-700">I have read and agree to the Terms of Service</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <button
            onClick={handleAccept}
            disabled={!accepted || submitting}
            className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Accepting...' : 'Accept & Continue'}
          </button>
        </div>

        <p className="text-center text-sm text-gray-400 mt-4">
          Questions? Contact us at info@scientistintheloop.com
        </p>
      </div>
    </div>
  )
}
